import { AfterViewInit, Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { MatCard } from '@angular/material/card';
import { OrganizationService } from '../../services/organization.service';
import { PaginatedDataSource } from '../../model/pagination/paginated-data-source.model';
import { Organization } from '../../model/organization/organization.model';
import { MatTableModule } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatChip, MatChipSet } from '@angular/material/chips';
import { OrganizationStatus } from '../../model/organization/organization-status.enum';
import { MatIcon } from '@angular/material/icon';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { debounceTime, Subscription } from 'rxjs';
import { MatInput } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { RouterLink } from '@angular/router';
import { OrganizationGoal } from '../../model/organization/organization-goal.model';
import { OrganizationReach } from '../../model/organization/organization-reach.model';
import { OrganizationProvince } from '../../model/organization/organization-province.model';
import { OrganizationHeritageType } from '../../model/organization/organization-heritage-type.model';
import { OrganizationImpactType } from '../../model/organization/organization-impact-type.model';
import { GoalService } from '../../services/goal.service';
import { ReachService } from '../../services/reach.service';
import { ProvinceService } from '../../services/province.service';
import { HeritageTypeService } from '../../services/heritage-type.service';
import { ImpactTypeService } from '../../services/impact-type.service';
import { MatTooltip } from '@angular/material/tooltip';
import { MaxWordsPipe } from '../../pipes/max-words.pipe';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  DeleteOrganizationDialog,
} from '../../create-edit-organization/delete-organization-dialog/delete-organization-dialog';

@Component({
  selector: 'app-organization-list',
  imports: [
    MatCard,
    MatTableModule,
    MatPaginator,
    AsyncPipe,
    MatProgressBar,
    MatChipSet,
    MatChip,
    MatIcon,
    DatePipe,
    MatFormFieldModule,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    MatOption,
    MatInput,
    MatButton,
    MatCheckbox,
    RouterLink,
    MatTooltip,
    MaxWordsPipe,
    MatIconButton,
  ],
  templateUrl: './organization-list.html',
  styleUrl: './organization-list.scss',
})
export class OrganizationList implements AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  // Services
  private organizationService = inject(OrganizationService);
  private goalService = inject(GoalService);
  private reachService = inject(ReachService);
  private provinceService = inject(ProvinceService);
  private heritageTypeService = inject(HeritageTypeService);
  private impactTypeService = inject(ImpactTypeService);
  private matDialog = inject(MatDialog);
  private matSnackBar = inject(MatSnackBar);
  // Pagination
  public dataSource!: PaginatedDataSource<Organization>;
  public displayedColumns: string[] = ['name', 'goal', 'reach', 'province', 'impactTypes', 'status', 'actions'];
  public OrganizationStatus = OrganizationStatus;
  private pageIndex = 1;
  private pageSize = 10;
  // Filters
  // Search query
  public searchQueryInput: FormControl = new FormControl();
  private searchQuery = '';
  // Goal filter
  public goalSelect = new FormControl<OrganizationGoal[] | null>([]);
  public goalList: OrganizationGoal[] = [];
  private selectedGoalIdList: string[] = [];
  // Reach filter
  public reachSelect = new FormControl<OrganizationReach[] | null>([]);
  public reachList: OrganizationReach[] = [];
  private selectedReachIdList: string[] = [];
  // Province filter
  public provinceSelect = new FormControl<OrganizationProvince[] | null>([]);
  public provinceList: OrganizationProvince[] = [];
  private selectedProvinceIdList: string[] = [];
  // Heritage type filter
  public heritageTypeSelect = new FormControl<OrganizationHeritageType[] | null>([]);
  public heritageTypeList: OrganizationHeritageType[] = [];
  private selectedHeritageTypeIdList: string[] = [];
  // Impact type filter
  public impactTypeSelect = new FormControl<OrganizationImpactType[] | null>([]);
  public impactTypeList: OrganizationImpactType[] = [];
  private selectedImpactTypeIdList: string[] = [];
  // Status filter
  public statusSelect = new FormControl<OrganizationStatus | string>('');
  private selectedStatus: OrganizationStatus | string = '';
  // Is active
  public isActiveCheckbox = new FormControl<boolean>(true);
  private isActive = true;
  // Subscription management
  private subscriptionList: Subscription[] = [];
  // Reset state
  private resetFiltersInProgress = false;
  // Local storage key for filter persistence
  private static readonly STORAGE_KEY = 'organization-list-filters';

  // UI settings
  private readonly goalColorMap: Record<string, { color: string; background: string; border: string }> = {
    'cultural-preservation': {color: '#fc0', background: '#ffcc001a', border: '#ffcc0033'},     // Cultuurbehoud
    'climate-nature-landscape': {color: '#9b3965', background: '#9b39651a', border: '#9b396533'}, // Klimaat, natuur, en landschap behoud
    'culture-and-tradition': {color: '#97a748', background: '#97a7481a', border: '#97a74833'}, // Cultuur en tradities laten zien
    'umbrella-organization': {color: '#666', background: '#6666661a', border: '#666663'},     // Overkoepelende of Intermediaire organisatie
    'bringing-people-together': {color: '#f90', background: '#ff99001a', border: '#ff990033'},     // Mensen bij elkaar brengen
    'adapting-renewing-heritage': {color: '#35a9c5', background: '#35a9c51a', border: '#35a9c533'}, // Erfgoed aanpassen en vernieuwen
  };
  private readonly defaultGoalColor = {color: '#666', background: '#6666661a', border: '#6663'};

  public goalColor(goal: { class: string }): { color: string; background: string; border: string } {
    return this.goalColorMap[goal.class] ?? this.defaultGoalColor;
  }

  constructor() {
    this.dataSource = new PaginatedDataSource<Organization>(this.organizationService);
    this.loadFiltersData();
    this.observeFilterChanges();
  }

  public ngAfterViewInit(): void {
    this.paginator?.page.subscribe((pageEvent: PageEvent) => {
      this.pageSize = pageEvent.pageSize;
      this.pageIndex = pageEvent.pageIndex + 1;
      this.loadPaginatedData();
    });

    // Reset to page 1 if current page has no results (e.g., after filter is applied)
    this.subscriptionList.push(
      this.dataSource.paginatedData$.subscribe((response) => {
        if (response.items.length === 0 && this.pageIndex > 1) {
          this.pageIndex = 1;
          this.paginator.pageIndex = 0;
          this.loadPaginatedData();
        }
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptionList.forEach(subscription => subscription.unsubscribe());
  }

  private loadFiltersData(): void {
    // Track loading state for each filter data source
    let goalsLoaded = false;
    let reachesLoaded = false;
    let provincesLoaded = false;
    let heritageTypesLoaded = false;
    let impactTypesLoaded = false;

    const tryRestoreFilters = (): void => {
      if (goalsLoaded && reachesLoaded && provincesLoaded && heritageTypesLoaded && impactTypesLoaded) {
        this.restoreFiltersFromStorage();
      }
    };

    // Goal
    this.goalService.getGoalList()
      .subscribe((goalList: OrganizationGoal[]): void => {
        this.goalList = goalList;
        goalsLoaded = true;
        tryRestoreFilters();
      });
    // Reach
    this.reachService.getReachList()
      .subscribe((reachList: OrganizationReach[]): void => {
        this.reachList = reachList;
        reachesLoaded = true;
        tryRestoreFilters();
      });
    // Province
    this.provinceService.getProvinceList()
      .subscribe((provinceList: OrganizationProvince[]): void => {
        this.provinceList = provinceList;
        provincesLoaded = true;
        tryRestoreFilters();
      });
    // Heritage type
    this.heritageTypeService.getHeritageTypeList()
      .subscribe((heritageTypeList: OrganizationHeritageType[]): void => {
        this.heritageTypeList = heritageTypeList;
        heritageTypesLoaded = true;
        tryRestoreFilters();
      });
    // Impact type
    this.impactTypeService.getImpactTypeList()
      .subscribe((impactTypeList: OrganizationImpactType[]): void => {
        this.impactTypeList = impactTypeList;
        impactTypesLoaded = true;
        tryRestoreFilters();
      });
  }

  private observeFilterChanges(): void {
    this.subscriptionList.push(
      // Search query
      this.searchQueryInput.valueChanges.pipe(debounceTime(300)).subscribe((value: string) => {
        this.searchQuery = value;
        this.saveFiltersToStorage();
        this.loadPaginatedData();
      }),
      // Goal
      this.goalSelect.valueChanges.subscribe((selectedGoalList: OrganizationGoal[] | null): void => {
        if (selectedGoalList !== null) {
          this.selectedGoalIdList = selectedGoalList.map((goal: OrganizationGoal) => goal.id);
          this.saveFiltersToStorage();
          this.loadPaginatedData();
        }
      }),
      // Reach
      this.reachSelect.valueChanges.subscribe((selectedReachList: OrganizationReach[] | null): void => {
        if (selectedReachList !== null) {
          this.selectedReachIdList = selectedReachList.map((reach: OrganizationReach) => reach.id);
          this.saveFiltersToStorage();
          this.loadPaginatedData();
        }
      }),
      // Province
      this.provinceSelect.valueChanges.subscribe((selectedProvinceList: OrganizationProvince[] | null): void => {
        if (selectedProvinceList !== null) {
          this.selectedProvinceIdList = selectedProvinceList.map((province: OrganizationProvince) => province.id);
          this.saveFiltersToStorage();
          this.loadPaginatedData();
        }
      }),
      // Heritage type
      this.heritageTypeSelect.valueChanges.subscribe((selectedHeritageTypeList: OrganizationHeritageType[] | null): void => {
        if (selectedHeritageTypeList !== null) {
          this.selectedHeritageTypeIdList = selectedHeritageTypeList.map((heritageType: OrganizationHeritageType) => heritageType.id);
          this.saveFiltersToStorage();
          this.loadPaginatedData();
        }
      }),
      // Impact type
      this.impactTypeSelect.valueChanges.subscribe((selectedImpactTypeList: OrganizationImpactType[] | null): void => {
        if (selectedImpactTypeList !== null) {
          this.selectedImpactTypeIdList = selectedImpactTypeList.map((impactType: OrganizationImpactType) => impactType.id);
          this.saveFiltersToStorage();
          this.loadPaginatedData();
        }
      }),
      // Status
      this.statusSelect.valueChanges.subscribe((newStatus: OrganizationStatus | string | null): void => {
        if (newStatus !== null) {
          this.selectedStatus = newStatus;
          this.saveFiltersToStorage();
          this.loadPaginatedData();
        }
      }),
      // Active
      this.isActiveCheckbox.valueChanges.subscribe((isActiveCheckbox: boolean | null): void => {
        this.isActive = isActiveCheckbox ?? false;
        this.saveFiltersToStorage();
        this.loadPaginatedData();
      }),
    );
  }

  /**
   * Save current filter state to local storage
   */
  private saveFiltersToStorage(): void {
    if (this.resetFiltersInProgress) {
      return;
    }
    const filterState = {
      searchQuery: this.searchQuery,
      selectedGoalIdList: this.selectedGoalIdList,
      selectedReachIdList: this.selectedReachIdList,
      selectedProvinceIdList: this.selectedProvinceIdList,
      selectedHeritageTypeIdList: this.selectedHeritageTypeIdList,
      selectedImpactTypeIdList: this.selectedImpactTypeIdList,
      selectedStatus: this.selectedStatus,
      isActive: this.isActive,
    };
    localStorage.setItem(OrganizationList.STORAGE_KEY, JSON.stringify(filterState));
  }

  /**
   * Restore filter state from local storage
   */
  private restoreFiltersFromStorage(): void {
    const stored = localStorage.getItem(OrganizationList.STORAGE_KEY);
    if (!stored) {
      // No stored filters, load with default state
      this.loadPaginatedData();
      return;
    }

    try {
      const filterState = JSON.parse(stored);
      this.resetFiltersInProgress = true;

      // Restore search query
      if (filterState.searchQuery) {
        this.searchQuery = filterState.searchQuery;
        this.searchQueryInput.setValue(filterState.searchQuery, {emitEvent: false});
      }

      // Restore goal filter
      if (filterState.selectedGoalIdList?.length > 0) {
        this.selectedGoalIdList = filterState.selectedGoalIdList;
        const selectedGoals = this.goalList.filter(
          (goal: OrganizationGoal) => filterState.selectedGoalIdList.includes(goal.id),
        );
        this.goalSelect.setValue(selectedGoals, {emitEvent: false});
      }

      // Restore reach filter
      if (filterState.selectedReachIdList?.length > 0) {
        this.selectedReachIdList = filterState.selectedReachIdList;
        const selectedReaches = this.reachList.filter(
          (reach: OrganizationReach) => filterState.selectedReachIdList.includes(reach.id),
        );
        this.reachSelect.setValue(selectedReaches, {emitEvent: false});
      }

      // Restore province filter
      if (filterState.selectedProvinceIdList?.length > 0) {
        this.selectedProvinceIdList = filterState.selectedProvinceIdList;
        const selectedProvinces = this.provinceList.filter(
          (province: OrganizationProvince) => filterState.selectedProvinceIdList.includes(province.id),
        );
        this.provinceSelect.setValue(selectedProvinces, {emitEvent: false});
      }

      // Restore heritage type filter
      if (filterState.selectedHeritageTypeIdList?.length > 0) {
        this.selectedHeritageTypeIdList = filterState.selectedHeritageTypeIdList;
        const selectedHeritageTypes = this.heritageTypeList.filter(
          (heritageType: OrganizationHeritageType) => filterState.selectedHeritageTypeIdList.includes(heritageType.id),
        );
        this.heritageTypeSelect.setValue(selectedHeritageTypes, {emitEvent: false});
      }

      // Restore impact type filter
      if (filterState.selectedImpactTypeIdList?.length > 0) {
        this.selectedImpactTypeIdList = filterState.selectedImpactTypeIdList;
        const selectedImpactTypes = this.impactTypeList.filter(
          (impactType: OrganizationImpactType) => filterState.selectedImpactTypeIdList.includes(impactType.id),
        );
        this.impactTypeSelect.setValue(selectedImpactTypes, {emitEvent: false});
      }

      // Restore status filter
      if (filterState.selectedStatus) {
        this.selectedStatus = filterState.selectedStatus;
        this.statusSelect.setValue(filterState.selectedStatus, {emitEvent: false});
      }

      // Restore active checkbox
      if (filterState.isActive !== undefined) {
        this.isActive = filterState.isActive;
        this.isActiveCheckbox.setValue(filterState.isActive, {emitEvent: false});
      }

      this.resetFiltersInProgress = false;
    } catch (e) {
      console.error('Failed to restore filters from storage:', e);
      localStorage.removeItem(OrganizationList.STORAGE_KEY);
    }

    // Load data with restored filters
    this.loadPaginatedData();
  }

  /**
   * Load paginated and filtered data from the back-end
   */
  public loadPaginatedData(): void {
    // Avoid loading when restoring the filters
    if (this.resetFiltersInProgress) {
      return;
    }
    // General filters and pagination
    const params: any = {
      search: this.searchQuery,
      page: this.pageIndex,
      pageSize: this.pageSize,
      'heritageGoals[]': this.selectedGoalIdList,
      'heritageTypes[]': this.selectedHeritageTypeIdList,
      'impactTypes[]': this.selectedImpactTypeIdList,
      'provinces[]': this.selectedProvinceIdList,
      'scopes[]': this.selectedReachIdList,
      'active': this.isActive,
    };
    // Check conditional filter Status
    if (this.selectedStatus !== '') {
      params.status = this.selectedStatus;
    }
    // Load data
    this.dataSource.loadData({
      params,
    });
  }

  /**
   * Clear all filters
   */
  public clearFilters(): void {
    this.resetFiltersInProgress = true;
    this.searchQueryInput.reset('');
    this.goalSelect.reset([]);
    this.reachSelect.reset([]);
    this.provinceSelect.reset([]);
    this.heritageTypeSelect.reset([]);
    this.impactTypeSelect.reset([]);
    this.statusSelect.reset('');
    this.isActiveCheckbox.reset(true);
    this.resetFiltersInProgress = false;

    // also reset your "selected…" variables if needed
    this.searchQuery = '';
    this.selectedGoalIdList = [];
    this.selectedReachIdList = [];
    this.selectedProvinceIdList = [];
    this.selectedHeritageTypeIdList = [];
    this.selectedImpactTypeIdList = [];
    this.selectedStatus = '';
    this.isActive = true;

    // Clear stored filters
    localStorage.removeItem(OrganizationList.STORAGE_KEY);

    // Reset page
    this.pageIndex = 1;

    // Reload data
    this.loadPaginatedData();
  }

  /**
   * Ask for confirmation and delete the organization, keeping the current filters
   */
  public deleteOrganization(organization: Organization, event: MouseEvent): void {
    // The row itself navigates to the edit screen, so the click must not bubble up
    event.stopPropagation();

    const dialogRef = this.matDialog.open(DeleteOrganizationDialog);

    dialogRef.afterClosed().subscribe((confirmed: boolean | undefined): void => {
      if (confirmed !== true) {
        return;
      }
      this.organizationService.deleteOrganization(organization.id)
        .subscribe({
          next: (): void => {
            this.matSnackBar.open('Organisatie succesvol verwijderd.', '', {duration: 3000});
            // Reload the list with the filters that are currently applied
            this.loadPaginatedData();
          },
          error: (): void => {
            this.matSnackBar.open('Een fout heeft opgetreden, probeer het nogmaals.', '', {duration: 3000});
          },
        });
    });
  }
}
